import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  PruefstatusDashboard,
  TwinEventCard,
  MockDataLabel,
  type TwinEvent,
} from '@hofsync/design-system';

import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { usePruefstatus, usePruefstatusArea, useEventActions, useFarmLenses } from '../hooks/useTwin';
import { useTwinSSE } from '../hooks/useTwinSSE';
import { MOCK_AREA_EVENT_TYPES, MOCK_PRUEFSTATUS, MOCK_EVENTS } from '../mocks/betrieb-mueller/twin';
import { lensIdByEntityType } from '../components/twinLinks/lensTargets';
import './PruefstatusPage.css';

/**
 * The object an event is about. UDB returns parallel lists (`entity_types[i]`
 * is the type of `entity_ids[i]`); the scalar pair exists on mock/SSE payloads
 * only (FR-017 #7b, review r1 P1-1).
 */
function eventObject(event: TwinEvent): { entityType: string; entityId: string } | null {
  const entityType = event.entity_types?.[0] ?? event.entity_type;
  const entityId = event.entity_ids?.[0] ?? event.entity_id;
  return entityType && entityId ? { entityType, entityId } : null;
}

const PruefstatusPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasFlag } = useFeatureFlags();
  const useLiveData = hasFlag('twin.pruefstatus');

  // SSE for live updates
  useTwinSSE({ enabled: useLiveData });

  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState<string | null>(null);
  const [mockEventStates, setMockEventStates] = useState<Record<string, string>>({});

  // Live data hooks (only fetch when twin flag is active)
  const { data: livePruefstatus, isLoading: pruefstatusLoading } = usePruefstatus();
  // FR-017 #7b §5 — the drill-down is the area endpoint: the backend selects the
  // area's open events BY EVENT TYPE, so the list is exactly what the area counted.
  const { data: liveArea, isLoading: areaLoading } = usePruefstatusArea(
    useLiveData && selectedArea ? selectedArea : '',
  );
  const { data: lenses } = useFarmLenses(useLiveData);
  const { acknowledge, dismiss, resolve } = useEventActions();

  // Resolve data source — live mode: never fall back to mock data
  const pruefstatus = useLiveData
    ? livePruefstatus
    : MOCK_PRUEFSTATUS;

  const handleAreaClick = useCallback((areaId: string) => {
    setSelectedArea((prev) => (prev === areaId ? null : areaId));
  }, []);

  const areaEvents = useMemo(() => {
    if (!selectedArea) return [];
    if (useLiveData) return liveArea?.events ?? [];
    // Demo: the same selection by event type, over the mock events.
    const types = MOCK_AREA_EVENT_TYPES[selectedArea] ?? [];
    return MOCK_EVENTS.filter(
      (e) => types.includes(e.event_type) && e.status !== 'resolved' && !mockEventStates[e.event_id],
    );
  }, [selectedArea, useLiveData, liveArea, mockEventStates]);

  // „Zum Objekt" — only for an object some farm lens shows; anything else would be a dead link.
  const lensByType = useMemo(() => lensIdByEntityType(lenses), [lenses]);
  const objectRoute = useCallback(
    (event: TwinEvent): string | null => {
      const target = eventObject(event);
      const lensId = target ? lensByType.get(target.entityType) : undefined;
      if (!target || !lensId) return null;
      return `/farm?${new URLSearchParams({ lense: lensId, item: target.entityId }).toString()}`;
    },
    [lensByType],
  );

  const handleAcknowledge = useCallback((eventId: string) => {
    if (useLiveData) {
      acknowledge.mutate(eventId);
    } else {
      setMockEventStates((prev) => ({ ...prev, [eventId]: 'acknowledged' }));
    }
  }, [useLiveData, acknowledge]);

  const handleDismiss = useCallback((eventId: string) => {
    if (useLiveData) {
      dismiss.mutate(eventId);
    } else {
      setMockEventStates((prev) => ({ ...prev, [eventId]: 'dismissed' }));
    }
  }, [useLiveData, dismiss]);

  const handleResolve = useCallback((eventId: string) => {
    if (useLiveData) {
      resolve.mutate(eventId);
    } else {
      setMockEventStates((prev) => ({ ...prev, [eventId]: 'resolved' }));
    }
  }, [useLiveData, resolve]);

  if (useLiveData && pruefstatusLoading) {
    return (
      <div className="pruefstatus-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <p style={{ color: '#6b7280' }}>Prüfstatus wird geladen...</p>
      </div>
    );
  }

  if (!pruefstatus) {
    return (
      <div className="pruefstatus-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
        <p style={{ color: '#6b7280' }}>Keine Prüfstatus-Daten verfügbar.</p>
      </div>
    );
  }

  return (
    <div className="pruefstatus-page">
      {!useLiveData && (
        <MockDataLabel
          text={t('twin.pruefstatusDemo')}
          position="top-right"
          color="blue"
        />
      )}
      <PruefstatusDashboard
        pruefstatus={pruefstatus}
        onAreaClick={handleAreaClick}
      />
      {selectedArea && areaEvents.length > 0 && (
        <div className="pruefstatus-page__events">
          <h3 className="pruefstatus-page__events-title">
            Beitragende Ereignisse:{' '}
            {pruefstatus.areas.find((a) => a.area_id === selectedArea)?.area_name}
          </h3>
          <div className="pruefstatus-page__events-list">
            {areaEvents.map((event) => {
              const route = objectRoute(event);
              return (
                <TwinEventCard
                  key={event.event_id}
                  event={event}
                  onAcknowledge={handleAcknowledge}
                  onDismiss={handleDismiss}
                  onResolve={handleResolve}
                  onOpenObject={route ? () => navigate(route) : undefined}
                />
              );
            })}
          </div>
        </div>
      )}
      {selectedArea && areaEvents.length === 0 && (
        <div className="pruefstatus-page__events">
          <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
            {useLiveData && areaLoading
              ? 'Ereignisse werden geladen...'
              : 'Keine offenen Ereignisse in diesem Bereich.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PruefstatusPage;
